import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { sanitizeSvg } from "@/lib/sanitize";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ChartLineUp, CircleNotch, Plus, Trash, ArrowRight, UploadSimple, FilePdf } from "@phosphor-icons/react";

const inputCls = "h-7 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]";

const PointTable = ({ title, rows, setRows, color, testid }) => {
  const edit = (i, f, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  const add = () => setRows((rs) => [...rs, { sta: "", elev: "" }]);
  const rm = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  return (
    <div className="flex flex-col gap-1.5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className="text-[11px] font-mono text-[var(--text)]">{title}</span>
        <Button onClick={add} size="sm" variant="ghost" data-testid={`${testid}-add`}
          className="h-6 px-1.5 ml-auto text-[10px] gap-1 text-[var(--text-dim)] hover:text-[var(--text)]"><Plus size={12} />Add</Button>
      </div>
      <div className="flex flex-col gap-1 max-h-[150px] overflow-auto">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`${testid}-row-${i}`}>
            <span className="w-4 text-[10px] font-mono text-[var(--text-dim)]">{i + 1}</span>
            <Input value={r.sta} onChange={(e) => edit(i, "sta", e.target.value)} placeholder="station (ft)" className={inputCls} data-testid={`${testid}-sta-${i}`} />
            <Input value={r.elev} onChange={(e) => edit(i, "elev", e.target.value)} placeholder="elevation" className={inputCls} data-testid={`${testid}-elev-${i}`} />
            <button onClick={() => rm(i)} className="text-[var(--text-dim)] hover:text-[var(--alert)]" data-testid={`${testid}-rm-${i}`}><Trash size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RoadProfile = ({ onUsePrompt, clientName, clientStandards }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [existing, setExisting] = useState([{ sta: "0", elev: "100" }, { sta: "100", elev: "101.5" }, { sta: "200", elev: "103" }]);
  const [proposed, setProposed] = useState([{ sta: "0", elev: "100" }, { sta: "200", elev: "104" }]);
  const [data, setData] = useState(null);
  const [vcLength, setVcLength] = useState("0");
  const [width, setWidth] = useState("0");
  const [sideSlope, setSideSlope] = useState("0");
  const [csvText, setCsvText] = useState("");
  const [importTarget, setImportTarget] = useState("existing");
  const [storePoints, setStorePoints] = useState([]);
  const [startPt, setStartPt] = useState("");
  const [endPt, setEndPt] = useState("");
  const [mapSvg, setMapSvg] = useState("");
  const timer = useRef();
  const fileRef = useRef();

  const loadStorePoints = async () => {
    try {
      setStorePoints(await api.cogoPoints());
      const m = await api.cogoPointsMap("", "");
      setMapSvg(m.svg || "");
    } catch (e) { console.debug("loadStorePoints failed (best-effort)", e); }
  };

  const refreshMap = async (s, e) => {
    try { const m = await api.cogoPointsMap(s, e); setMapSvg(m.svg || ""); } catch (err) { console.debug("refreshMap failed (best-effort)", err); }
  };

  const buildFromPoints = async () => {
    if (!startPt || !endPt) { toast.error("Pick a start and end point."); return; }
    setBusy(true);
    try {
      const r = await api.profileFromPoints(startPt, endPt);
      setExisting(r.existing.map((p) => ({ sta: String(p.sta), elev: String(p.elev) })));
      setData(r.summary);
      toast.success(`Built profile from ${r.used} stored point${r.used === 1 ? "" : "s"} (baseline ${r.baseline_ft} ft)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build a profile from the stored points.");
    } finally { setBusy(false); }
  };

  const num = (rows) => rows.map((r) => ({ sta: Number(r.sta), elev: Number(r.elev) }))
    .filter((r) => Number.isFinite(r.sta) && Number.isFinite(r.elev));

  const parseCsv = (text) => {
    const rows = [];
    for (const line of (text || "").split(/\r?\n/)) {
      const parts = line.split(/[,\t; ]+/).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      const sta = parseFloat(parts[0]);
      const elev = parseFloat(parts[1]);
      if (Number.isFinite(sta) && Number.isFinite(elev)) rows.push({ sta: String(sta), elev: String(elev) });
    }
    return rows;
  };

  const applyCsv = (text) => {
    const rows = parseCsv(text);
    if (!rows.length) { toast.error("No station,elevation pairs found. Use rows like: 0,100.5"); return; }
    if (importTarget === "existing") setExisting(rows); else setProposed(rows);
    toast.success(`Loaded ${rows.length} point${rows.length === 1 ? "" : "s"} into ${importTarget}`);
    setTimeout(preview, 50);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { applyCsv(await file.text()); } catch { toast.error("Could not read the file."); }
    finally { e.target.value = ""; }
  };

  const preview = () => {
    clearTimeout(timer.current);
    setBusy(true);
    timer.current = setTimeout(async () => {
      try { setData(await api.profilePreview({ existing: num(existing), proposed: num(proposed), vc_length: Number(vcLength) || 0, width: Number(width) || 0, side_slope: Number(sideSlope) || 0 })); }
      catch { toast.error("Could not build the profile preview."); }
      finally { setBusy(false); }
    }, 300);
  };

  const sendToGen = () => {
    const ex = num(existing), pr = num(proposed);
    if (!ex.length) { toast.error("Enter existing ground station/elevation points first."); return; }
    const exLines = ex.map((p) => `  STA ${p.sta}, ELEV ${p.elev}`).join("\n");
    const prLines = pr.length ? pr.map((p) => `  PVI STA ${p.sta}, ELEV ${p.elev}`).join("\n") : "  (none — draw existing ground only)";
    const vc = Number(vcLength) || 0;
    const vcNote = vc > 0
      ? `Apply parabolic VERTICAL CURVES of length ${vc} ft at each interior PVI (compute BVC/EVC, curve elevations, and high/low points).`
      : "Connect PVIs with straight grade tangents (no vertical curves).";
    const prompt =
      "Draw a DRIVEWAY / ROAD PROFILE (station vs. elevation) grid on the client's profile layers and text styles. " +
      "Use a vertical exaggeration of 10x and label station gridlines (e.g. 1+00) along the bottom and elevations up the left. " +
      `Plot the EXISTING GROUND profile through these points, and the PROPOSED grade through the given PVIs. ${vcNote} ` +
      "Label each grade %, PVI stations/elevations, high/low points, and cut/fill between existing and proposed.\n\n" +
      `EXISTING GROUND (station, elevation):\n${exLines}\n\nPROPOSED PVIs (station, elevation):\n${prLines}`;
    onUsePrompt?.(prompt);
    setOpen(false);
    toast.success("Profile sent to the generator prompt");
  };

  const downloadPdf = async () => {
    const ex = num(existing);
    if (!ex.length) { toast.error("Enter existing ground points first."); return; }
    setReporting(true);
    const t = toast.loading("Building profile sheet…");
    try {
      const project = { firm: clientName || "", logo: clientStandards?.branding?.logo || "", client: clientName || "" };
      const blob = await api.profileReportPdf({ existing: ex, proposed: num(proposed), vc_length: Number(vcLength) || 0, width: Number(width) || 0, side_slope: Number(sideSlope) || 0, project });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "road_profile.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Profile sheet downloaded", { id: t });
    } catch { toast.error("Could not build the profile sheet.", { id: t }); }
    finally { setReporting(false); }
  };

  const svg = data?.svg ? sanitizeSvg(data.svg) : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { preview(); loadStorePoints(); } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="road-profile-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <ChartLineUp size={14} weight="bold" /> Road Profile
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="road-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><ChartLineUp size={18} weight="bold" className="text-[var(--primary)]" />Road / Driveway Profile</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Enter station-elevation points, preview the profile grid live, then generate the LISP.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-3">
            <PointTable title="Existing ground" rows={existing} setRows={setExisting} color="#30D158" testid="prof-existing" />
            <PointTable title="Proposed PVIs" rows={proposed} setRows={setProposed} color="#FF9F0A" testid="prof-proposed" />
            <div className="flex flex-col gap-1.5 border-t border-[var(--border)]/40 pt-2">
              <div className="flex items-center gap-1.5">
                <select value={importTarget} onChange={(e) => setImportTarget(e.target.value)} data-testid="prof-import-target"
                  className="h-7 px-1 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[10px] text-[var(--text)]">
                  <option value="existing">→ Existing</option>
                  <option value="proposed">→ Proposed</option>
                </select>
                <input type="file" ref={fileRef} accept=".csv,.txt" className="hidden" onChange={onFile} data-testid="prof-csv-file" />
                <Button onClick={() => fileRef.current?.click()} size="sm" variant="outline" data-testid="prof-csv-upload"
                  className="h-7 rounded-[2px] text-[10px] gap-1 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><UploadSimple size={12} />Upload CSV</Button>
                <span className="text-[10px] font-mono text-[var(--text-dim)]">or paste sta,elev ↓</span>
              </div>
              <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="prof-csv-text"
                placeholder="0,100.5&#10;50,101.2&#10;100,102.8" className="min-h-[52px] resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <Button onClick={() => applyCsv(csvText)} size="sm" variant="outline" data-testid="prof-csv-fill"
                className="h-7 rounded-[2px] text-[10px] gap-1 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><ArrowRight size={12} />Fill table from pasted CSV</Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-[var(--text-dim)]">Vertical curve length</span>
              <Input value={vcLength} onChange={(e) => setVcLength(e.target.value)} placeholder="ft (0 = none)" data-testid="prof-vc-length"
                className="h-7 w-20 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <span className="text-[11px] font-mono text-[var(--text-dim)] ml-2">Width</span>
              <Input value={width} onChange={(e) => setWidth(e.target.value)} placeholder="ft" data-testid="prof-width"
                className="h-7 w-16 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <span className="text-[11px] font-mono text-[var(--text-dim)] ml-1">Slope</span>
              <Input value={sideSlope} onChange={(e) => setSideSlope(e.target.value)} placeholder="H:1" data-testid="prof-side-slope"
                className="h-7 w-14 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <span className="text-[10px] font-mono text-[var(--text-dim)]">:1</span>
            </div>
            <div className="flex items-center gap-1.5 border-t border-[var(--border)]/40 pt-2" data-testid="prof-from-points">
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">From stored points</span>
              <select value={startPt} onChange={(e) => { setStartPt(e.target.value); refreshMap(e.target.value, endPt); }} data-testid="prof-start-pt"
                className="h-7 px-1 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[10px] text-[var(--text)] max-w-[90px]">
                <option value="">start…</option>
                {storePoints.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={endPt} onChange={(e) => { setEndPt(e.target.value); refreshMap(startPt, e.target.value); }} data-testid="prof-end-pt"
                className="h-7 px-1 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[10px] text-[var(--text)] max-w-[90px]">
                <option value="">end…</option>
                {storePoints.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Button onClick={buildFromPoints} size="sm" variant="outline" data-testid="prof-build-points"
                className="h-7 rounded-[2px] text-[10px] gap-1 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><ArrowRight size={12} />Build</Button>
            </div>
            {mapSvg && (
              <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] p-1 flex items-center justify-center [&_svg]:max-w-full [&_svg]:h-auto" data-testid="prof-point-map" dangerouslySetInnerHTML={{ __html: sanitizeSvg(mapSvg) }} />
            )}
            <Button onClick={preview} disabled={busy} data-testid="prof-preview-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {busy ? <CircleNotch size={14} className="animate-spin" /> : <ChartLineUp size={14} />} Update preview
            </Button>
          </div>
          <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] p-2 flex flex-col items-center justify-center min-h-[200px]" data-testid="prof-preview">
            {svg ? <div className="[&_svg]:max-w-full [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: svg }} />
              : <span className="text-[var(--text-dim)] text-xs">Preview appears here</span>}
          </div>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-[11px] font-mono flex-wrap" data-testid="prof-summary">
            <span className="text-[var(--text-dim)]">Length: <b className="text-[var(--text)]">{data.length_ft ?? "—"} ft</b></span>
            <span className="text-[var(--text-dim)]">Elev: <b className="text-[var(--text)]">{data.min_elev} → {data.max_elev}</b></span>
            {(data.grades || []).map((g, i) => (
              <span key={i} className="text-[var(--warn)]">{g.from}→{g.to}: {g.grade_pct >= 0 ? "+" : ""}{g.grade_pct}%</span>
            ))}
            {(data.high_low || []).map((hl, i) => (
              <span key={`hl-${i}`} className="text-[var(--alert)]" data-testid={`prof-hl-${i}`}>{hl.type.toUpperCase()} @ {hl.sta} = {hl.elev}</span>
            ))}
            {data.earthwork && (
              <span className="text-[var(--text-dim)] w-full" data-testid="prof-earthwork">
                Earthwork: <b className="text-[var(--alert)]">cut {data.earthwork.cut_area_sqft} ft²</b> · <b className="text-[var(--success)]">fill {data.earthwork.fill_area_sqft} ft²</b>
                {data.earthwork.cut_cy != null
                  ? <> · <b className="text-[var(--alert)]">{data.earthwork.cut_cy} cy cut</b> / <b className="text-[var(--success)]">{data.earthwork.fill_cy} cy fill</b> / net <b className="text-[var(--text)]">{data.earthwork.net_cy >= 0 ? "+" : ""}{data.earthwork.net_cy} cy</b> <span className="text-[var(--text-dim)]">({data.earthwork.method})</span></>
                  : <> · enter a width (+ side slope) for cy volumes</>}
              </span>
            )}
          </div>
        )}
        {data?.cut_fill?.length > 0 && (
          <div className="border border-[var(--border)] rounded-[3px] max-h-[150px] overflow-auto" data-testid="prof-cutfill">
            <div className="grid grid-cols-[1fr_80px_80px_90px_60px] gap-1 px-2 py-1 text-[10px] font-mono uppercase text-[var(--text-dim)] bg-[var(--surface-2)] sticky top-0">
              <span>station</span><span>existing</span><span>proposed</span><span>cut/fill</span><span>type</span>
            </div>
            {data.cut_fill.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_80px_90px_60px] gap-1 px-2 py-0.5 text-[11px] font-mono border-t border-[var(--border)]/30" data-testid={`prof-cf-${i}`}>
                <span>{r.sta}</span><span>{r.existing.toFixed(2)}</span><span>{r.proposed.toFixed(2)}</span>
                <span className={r.type === "fill" ? "text-[var(--success)]" : r.type === "cut" ? "text-[var(--alert)]" : "text-[var(--text-dim)]"}>{r.diff >= 0 ? "+" : ""}{r.diff.toFixed(2)}</span>
                <span className={r.type === "fill" ? "text-[var(--success)]" : r.type === "cut" ? "text-[var(--alert)]" : "text-[var(--text-dim)]"}>{r.type.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
        {data?.mass_haul?.svg && (
          <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] p-2 flex flex-col gap-1" data-testid="prof-masshaul">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Mass-haul · net {data.mass_haul.net_cy >= 0 ? "+" : ""}{data.mass_haul.net_cy} cy{data.mass_haul.balances?.length ? ` · balance @ ${data.mass_haul.balances.map((b) => b.sta).join(", ")}` : ""}</span>
            <div className="[&_svg]:max-w-full [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: sanitizeSvg(data.mass_haul.svg) }} />
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={sendToGen} data-testid="prof-use-btn"
            className="flex-1 h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            <ArrowRight size={15} /> Use this profile to generate LISP
          </Button>
          <Button onClick={downloadPdf} disabled={reporting} variant="outline" data-testid="prof-pdf-btn"
            className="h-9 rounded-[3px] gap-2 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] text-[var(--text)]">
            {reporting ? <CircleNotch size={15} className="animate-spin" /> : <FilePdf size={15} />} Profile sheet (PDF)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
